/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * POC COPY — source of truth lives at
 *   cds-ai-service/frontend/src/views/dev-chat/chat-stream/MessageList.jsx
 * See ./README.md for the extraction plan.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import AgentComparisonMessage from './AgentComparisonMessage.jsx';
import MessageItem from './MessageItem.jsx';
import { Box, useMediaQuery } from '@mui/material';

const SCROLL_PIN_THRESHOLD = 150;
const MIN_MESSAGES_FOR_SCROLL_SPACER = 2;

const MessageList = forwardRef(
  (
    {
      messages,
      isStreaming,
      onSendMessage,
      onSubmitFeedback,
      entityRef,
      chatChannelRef,
      showCopyButton = false,
      markdownDensity = 'compact',
      sendDisabled = false,
    },
    ref
  ) => {
    const viewportRef = useRef(null);
    const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
    const [hasNewMessages, setHasNewMessages] = useState(false);
    const previousMessageCountRef = useRef(0);
    const previousLastMessageRef = useRef(null);
    const isMobile = useMediaQuery('(max-width: 950px)');

    const showScrollSpacer = messages.length >= MIN_MESSAGES_FOR_SCROLL_SPACER;

    const getScrollElement = useCallback(() => {
      const viewport = viewportRef.current;

      if (!viewport) {
        return null;
      }

      return viewport.closest('[data-chat-messages-viewport="true"]') || viewport;
    }, []);

    const evaluatePinnedState = useCallback(() => {
      const element = getScrollElement();

      if (!element) {
        return;
      }

      const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      const nextIsPinned = distanceFromBottom <= SCROLL_PIN_THRESHOLD;

      setIsPinnedToBottom(nextIsPinned);

      if (nextIsPinned) {
        setHasNewMessages(false);
      }
    }, [getScrollElement]);

    const scrollToBottom = useCallback(
      (behavior = 'smooth') => {
        const element = getScrollElement();

        if (!element) {
          return;
        }

        element.scrollTo({
          top: element.scrollHeight,
          behavior: isStreaming ? 'auto' : behavior,
        });
      },
      [getScrollElement, isStreaming]
    );

    const handleScrollToBottomClick = useCallback(() => {
      scrollToBottom('smooth');
      setIsPinnedToBottom(true);
      setHasNewMessages(false);
    }, [scrollToBottom]);

    useImperativeHandle(
      ref,
      () => ({
        isPinnedToBottom,
        hasNewMessages,
        showScrollButton: !isPinnedToBottom && messages.length > 0,
        scrollToBottom: handleScrollToBottomClick,
      }),
      [isPinnedToBottom, hasNewMessages, messages.length, handleScrollToBottomClick]
    );

    useEffect(() => {
      const element = getScrollElement();

      if (!element) {
        return undefined;
      }

      const onScroll = () => {
        evaluatePinnedState();
      };

      element.addEventListener('scroll', onScroll);

      return () => {
        element.removeEventListener('scroll', onScroll);
      };
    }, [evaluatePinnedState, getScrollElement]);

    useEffect(() => {
      if (messages.length === 0) {
        previousMessageCountRef.current = 0;
        previousLastMessageRef.current = null;
        setHasNewMessages(false);

        return;
      }

      const lastMessage = messages[messages.length - 1];
      const previousLastMessage = previousLastMessageRef.current;
      const previousCount = previousMessageCountRef.current;
      const isNewMessage = messages.length > previousCount;
      const isNewUserMessage = isNewMessage && lastMessage?.role === 'user';
      const isNewAssistantMessage =
        isNewMessage && (lastMessage?.role === 'assistant' || lastMessage?.role === 'comparison');
      const isFirstUserMessage = messages.length === 1 && lastMessage?.role === 'user';
      const isMessageIdChanged =
        previousLastMessage?.id !== lastMessage?.id || previousLastMessage?.crn !== lastMessage?.crn;

      previousMessageCountRef.current = messages.length;
      previousLastMessageRef.current = lastMessage;

      if (isFirstUserMessage || isNewUserMessage) {
        const animationFrame = window.requestAnimationFrame(() => {
          scrollToBottom('smooth');
          setIsPinnedToBottom(true);
          setHasNewMessages(false);
        });

        return () => window.cancelAnimationFrame(animationFrame);
      }

      if (isNewAssistantMessage && isMessageIdChanged) {
        if (isPinnedToBottom) {
          const animationFrame = window.requestAnimationFrame(() => {
            scrollToBottom('smooth');
          });

          return () => window.cancelAnimationFrame(animationFrame);
        }

        setHasNewMessages(true);
      }

      if (isPinnedToBottom && isStreaming) {
        const animationFrame = window.requestAnimationFrame(() => {
          scrollToBottom('auto');
        });

        return () => window.cancelAnimationFrame(animationFrame);
      }

      return undefined;
    }, [messages, isPinnedToBottom, isStreaming, scrollToBottom]);

    return (
      <Box ref={viewportRef} sx={{ width: '100%' }}>
        {messages.map(message =>
          message?.role === 'comparison' ? (
            <AgentComparisonMessage
              key={message.crn ?? message.id}
              message={message}
              markdownDensity={markdownDensity}
            />
          ) : (
            <MessageItem
              key={message.crn ?? message.id}
              message={message}
              onSendMessage={onSendMessage}
              onSubmitFeedback={onSubmitFeedback}
              entityRef={entityRef}
              chatChannelRef={chatChannelRef}
              showCopyButton={showCopyButton}
              markdownDensity={markdownDensity}
              sendDisabled={sendDisabled}
            />
          )
        )}
        {showScrollSpacer && (
          <Box
            aria-hidden="true"
            sx={{
              height: isMobile ? '25vh' : '35vh',
              minHeight: isMobile ? '120px' : '180px',
              maxHeight: isMobile ? '200px' : '300px',
            }}
          />
        )}
      </Box>
    );
  }
);

MessageList.displayName = 'MessageList';

export default React.memo(MessageList);
